
import { trackEvent } from '@@/bits/analytics'
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import OzBadge, { OzBadgeColorScheme, OzBadgeSizePreset } from '@@/library/v4/components/OzBadge.vue'
import { n__ } from 'bits/intl'
import { Component, Prop, Vue } from 'bits/vue'
import ImageThumbnail from 'library/v4/components/ImageThumbnail.vue'
import OzBaseButton from 'library/v4/components/OzBaseButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from 'library/v4/components/OzPlainButton.vue'
import RelativeTime from 'library/v4/components/relative-time.vue'
import type { Library, WallCamelCase as Wall, Wall as WallSnakeCase } from 'types'

const MIN_CARD_WIDTH = 190

@Component({ components: { OzIcon, OzBaseButton, OzPlainButton, ImageThumbnail, RelativeTime, OzBadge } })
export default class OzWallCard extends Vue {
  OzPlainButtonColorScheme = OzPlainButtonColorScheme
  OzPlainButtonSizePreset = OzPlainButtonSizePreset
  OzBadgeColorScheme = OzBadgeColorScheme
  OzBadgeSizePreset = OzBadgeSizePreset
  isAppUsing = isAppUsing

  @Prop({ required: true, default: MIN_CARD_WIDTH }) cardWidth!: number
  @Prop({ required: true, default: [] }) wall!: Wall | WallSnakeCase
  @Prop({ default: 'updatedAt' }) wallTimestampType!: string
  @Prop({ default: true }) xMoreButton!: boolean
  @Prop({ default: false }) isTablet!: boolean
  @Prop({ default: '_blank' }) hrefTarget!: string
  @Prop({ required: false }) library?: Library

  isFocused = false
  isHoveringMoreButton = false

  handleMoreClick(event: PointerEvent): void {
    this.$emit('more', this.wall, event)
    trackEvent('Dashboard', 'Clicked More button on dashboard wall card')
  }

  get wallLinkLabel(): string {
    return this.__('Go to %{wallTitle} padlet', { wallTitle: this.wallTitle })
  }

  get wallThumbnailTitle(): string {
    return this.__('%{wallTitle} padlet thumbnail', { wallTitle: this.wallTitle })
  }

  get wallTitle(): string {
    return this.wall.title || this.__('Untitled padlet')
  }

  get wallTimestamp(): string {
    if (this.wallTimestampType === 'updatedAt') {
      return 'updated_at' in this.wall ? this.wall.updated_at : this.wall.updatedAt
    }
    return this.wall[this.wallTimestampType]
  }

  get accountDisplayName(): string {
    const displayName =
      'displayName' in this.wall.builder ? this.wall.builder.displayName : this.wall.builder.display_name
    return this.library ? this.library.name : displayName
  }

  get daysTillWallIsDeleted(): number {
    const daysLeft =
      'daysTillDeletion' in this.wall ? this.wall.daysTillDeletion : (this.wall as WallSnakeCase).days_till_deletion
    return daysLeft ?? 0
  }

  get daysTillDeletedDisplay(): string {
    return n__('%{num} day left', '%{num} days left', this.daysTillWallIsDeleted, { num: this.daysTillWallIsDeleted })
  }

  get wallDeletingSoon(): boolean {
    return this.daysTillWallIsDeleted <= 3
  }

  get xRelativeTimestamp(): boolean {
    return this.wallTimestampType !== 'trashedAt'
  }

  get xTemplate(): boolean {
    if (!isAppUsing('templates')) return false
    if ('isTemplate' in this.wall) {
      return this.wall.isTemplate === true
    }
    return false
  }

  get moreButtonTopOffset(): number {
    const wallThumbnailHeight = this.cardWidth / 2 // Since the wall thumbnail has an aspect ratio of 2, the height of the wall thumbnail is half of its width
    const gapBetweenWallThumbnailAndWallTitle = 12 // 12px = gap between thumbnail and wall name
    return wallThumbnailHeight + gapBetweenWallThumbnailAndWallTitle
  }

  showMoreMenu(e: PointerEvent): void {
    if (this.xMoreButton) {
      e.preventDefault()
      this.$emit('right-click', this.wall, e)
      trackEvent('right-click', { target: 'dashboard-wall-card' })
    }
  }

  handleWallThumbnailLoadingError(e: Error): void {
    captureException(e, {
      context: {
        component: 'OzWallCard',
        wallThumbnail: this.wall.thumbnail,
      },
    })
  }
}
